import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTOPLAY_MS = 4000;

/**
 * RelatedArtworks — Embla auto-rotating carousel of "more by same artist".
 *
 * Self-contained: owns its own Embla instance, autoplay interval, and
 * prev/next state. Parent passes in the filtered list of related artworks
 * and (optionally) the artist's display name for the heading.
 *
 * Renders nothing when `artworks` is empty — same behaviour as the old
 * inline `{relatedArtworks.length > 0 && (…)}` guard.
 */
const RelatedArtworks = ({ artworks = [], artistName, viewAllHref }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    slidesToScroll: 1,
    containScroll: 'trimSnaps',
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const autoplayRef = useRef(null);

  useEffect(() => {
    if (!emblaApi) return undefined;

    const onSelect = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };
    emblaApi.on('select', onSelect);
    onSelect();

    autoplayRef.current = setInterval(() => {
      if (emblaApi.canScrollNext()) emblaApi.scrollNext();
      else emblaApi.scrollTo(0);
    }, AUTOPLAY_MS);

    return () => {
      emblaApi.off('select', onSelect);
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, [emblaApi]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  if (!artworks || artworks.length === 0) return null;

  return (
    <div
      className="mt-20 pt-12 border-t border-neutral-200"
      data-testid="related-artworks-section"
    >
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-2xl font-light">
          More by {artistName || 'this artist'}
        </h2>
        <div className="flex items-center gap-4">
          {viewAllHref && (
            <Link
              to={viewAllHref}
              className="text-sm text-neutral-600 hover:text-black underline-offset-4 hover:underline"
            >
              View all works
            </Link>
          )}
          <div className="flex gap-2">
            <button
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              className={`p-2 border rounded-full transition-all ${
                canScrollPrev
                  ? 'border-neutral-300 hover:border-black'
                  : 'border-neutral-200 opacity-30 cursor-not-allowed'
              }`}
              aria-label="Previous"
              data-testid="related-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={scrollNext}
              disabled={!canScrollNext}
              className={`p-2 border rounded-full transition-all ${
                canScrollNext
                  ? 'border-neutral-300 hover:border-black'
                  : 'border-neutral-200 opacity-30 cursor-not-allowed'
              }`}
              aria-label="Next"
              data-testid="related-next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-6">
          {artworks.map((related) => (
            <div
              key={related.id}
              className="flex-[0_0_calc(100%/6-20px)] min-w-0 md:flex-[0_0_calc(100%/4-20px)] sm:flex-[0_0_calc(100%/2-20px)]"
            >
              <Link to={`/artwork/${related.slug || related.id}`} className="group block">
                <div className="aspect-[3/4] bg-neutral-50 mb-3 overflow-hidden flex items-center justify-center p-4">
                  <img
                    src={
                      related.imageUrl?.startsWith('http')
                        ? related.imageUrl
                        : `${API_URL}${related.imageUrl}`
                    }
                    alt={related.title}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-medium truncate group-hover:underline">
                    {related.title}
                  </h3>
                  <p className="text-xs text-neutral-500">{related.year}</p>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RelatedArtworks;
